import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, from, map, Observable, of, Subject, switchMap, tap } from 'rxjs';

import { AgentSource } from 'AgentApp/ts/interfaces/client-context';

import { AgentSuggestedPromptsService } from 'AgentApp/ts/services/agent-suggested-prompts.service';

import { IconModule } from 'Angular/features/directives/icon/icon.module';

import { PageContextClientTool, PageContextResult } from 'AgentApp/ts/client-tools/page-context.client-tool';

const SKELETON_ROWS = [0, 1, 2];

@Component({
    selector: 'ycn-agent-suggested-prompts',
    imports: [IconModule],
    templateUrl: './agent-suggested-prompts.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentSuggestedPromptsComponent implements OnInit {
    private readonly _suggestedPromptsService = inject(AgentSuggestedPromptsService);

    private readonly _pageContextClientTool = inject(PageContextClientTool);

    readonly source = input.required<AgentSource>();

    readonly promptSelected = output<string>();

    protected readonly prompts = signal<string[]>([]);

    protected readonly isLoading = signal(false);

    protected readonly skeletonRows = SKELETON_ROWS;

    private readonly _loadRequests = new Subject<boolean>();

    constructor() {
        this._loadRequests
            .pipe(
                tap(() => this.isLoading.set(true)),
                switchMap((regenerate) => {
                    const currentPrompts = this.prompts();
                    return this._pageContext().pipe(
                        switchMap((pageContext) =>
                            this._suggestedPromptsService.getSuggestedPrompts(
                                this._pageUrl(),
                                this._pageTitle(),
                                this.source(),
                                regenerate,
                                currentPrompts,
                                pageContext
                            )
                        ),
                        map((result) => result.prompts ?? []),
                        catchError(() => of(null))
                    );
                }),
                takeUntilDestroyed()
            )
            .subscribe((prompts) => {
                if (prompts) {
                    this.prompts.set(prompts);
                }
                this.isLoading.set(false);
            });
    }

    ngOnInit(): void {
        this._loadRequests.next(false);
    }

    refresh(): void {
        window.pendoTrack('Refresh Suggested Prompts', { source: this.source() });
        this._loadRequests.next(true);
    }

    selectPrompt(prompt: string): void {
        window.pendoTrack('Clicked Suggested Prompt', { prompt, source: this.source() });
        this.promptSelected.emit(prompt);
    }

    private _pageContext(): Observable<PageContextResult | null> {
        if (this.source() !== 'side_panel') {
            return of(null);
        }
        return from(this._pageContextClientTool.execute(null)).pipe(catchError(() => of(null)));
    }

    private _pageUrl(): string {
        return this.source() === 'side_panel' ? window.location.href : '';
    }

    private _pageTitle(): string {
        return this.source() === 'side_panel' ? document.title : '';
    }
}
